import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  periodWords,
  comparisonCaption,
  goalMoved,
  DAILY_HABITS_MET_LABEL,
  habitPeriodsCompleted,
  MOVED_BY_HABITS_LABEL,
} from '../taskVocab';
import type { ReviewGoal } from '../api';
import { QueryState } from './QueryState';
import { PeriodNav } from './PeriodNav';
import { amount } from './HabitHistory';
import { habitFigure, reviewAsOf } from '../habitFigure';
import { verdictWord } from '../habitVocab';
import { NO_NEXT_STEP_LABEL, showAllGoalsLabel } from '../goalVocab';
import { readableDay, readableRange } from '../dateLabels';

/**
 * A target that is the calendar's arithmetic, marked as such.
 *
 * An 18/week habit is 79.71 in August and 72 in February — one unchanged habit, two denominators,
 * because a habit shorter than the review is judged across the review with its target multiplied
 * by how many of its periods fit. That scaling is right and stays: numerator and denominator have
 * to come from one window, and a stated target would make the *verdict* depend on the length of
 * the month instead.
 *
 * So the figure is named rather than changed. `≈` says "derived", and the whole number says the
 * two decimal places were never a measurement — the maximizer read "0 / 79.71 this month" and
 * "72 / 392.94 this quarter" and could not tell which part of either was the habit.
 */
// Said by `habitFigure`, whose `approxTarget` is this row's `scaledTarget`.

/**
 * And the constant beside it, which is what a reader compares between periods.
 *
 * Empty for an unscaled row: `72 / 130 this month` on a monthly habit is the habit's own figure
 * and there is nothing derived to name. Naming it there would add noise to every row to explain
 * the rows that need it.
 */
const statedBasis = (h: {
  scaledTarget?: boolean;
  statedTarget?: number;
  statedPeriod?: string;
}): string =>
  h.scaledTarget && h.statedTarget !== undefined && h.statedPeriod
    ? ` (${amount(h.statedTarget, null)}/${h.statedPeriod})`
    : '';

/**
 * One period on or back, anchored at noon so no DST boundary is ever crossed (I-24).
 *
 * A week is seven days. A month and a quarter are not a fixed number of days, so those step by
 * month from the period's first day and let the server say where the period actually falls —
 * adding 30 days to the 31st is how paging lands twice on the same month.
 */
const shiftPeriod = (day: string, by: number, period: ReviewPeriod): string => {
  const at = new Date(`${day}T12:00:00Z`);
  if (period === 'week') at.setUTCDate(at.getUTCDate() + by * 7);
  else at.setUTCMonth(at.getUTCMonth() + by * (period === 'quarter' ? 3 : 1));
  return at.toISOString().slice(0, 10);
};
import {
  getMonthOverview,
  getWeekReview,
  type MonthDay,
  type WeekReview as WeekReviewData,
  createNote,
  updateNote,
  type ReviewPeriod,
  verdictTone,
} from '../api';

/**
 * The reflection screen (§7.2): a month grid that doubles as a heat map of what
 * actually happened, and the week review beside it.
 *
 * Every count here comes from the server, computed in the user's zone. The browser's
 * own idea of "today" is deliberately not consulted — that is what put yesterday's work
 * on today's row in Phase 3 (I-24).
 */

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const shiftMonth = (month: string, by: number): string => {
  const [year, m] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year!, m! - 1 + by, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (month: string) => {
  const [year, m] = month.split('-').map(Number);
  return `${MONTH_NAMES[m! - 1]} ${year}`;
};

function DayCell({
  day,
  selected,
  onSelect,
  peak,
}: {
  day: MonthDay;
  selected: boolean;
  onSelect: () => void;
  /** The busiest day in view, so the shade is a comparison within the month rather than absolute. */
  peak: number;
}) {
  /*
    Habits met as shade, tasks as dots.

    The cap was the wrong mechanism, not the wrong number. It was three, and on an account with 24
    habits every day of a month rendered as exactly three dots; it was raised to five, and on an
    account with 31 habits every day rendered as five dots and a "+N". Any cap loses the signal it
    is capping — a count that ranges 5 to 13 cannot be shown in a grid that stops at five.

    Shade has no ceiling. It is normalised to the busiest day *in view*, which is the convention a
    contribution graph uses: the exact numbers are in the tooltip and the accessible name, and what
    the grid is for is the shape of the month, which is a comparison between its own days.

    Tasks stay dots and stay capped: they are usually few, and two channels that look the same
    would be worse than one that is coarse.
  */
  const heat = peak > 0 ? day.habitsMet / peak : 0;
  const dots = Array.from({ length: Math.min(day.tasksCompleted, 4) }, (_, i) => (
    <span key={`t${i}`} className="dot task" />
  ));

  /*
    "2 of 4 daily habits met (1 by abstinence), 1 logged".

    Three things were being read off this cell as one number, and it printed only the numerator:
    "1 daily habit met" against Today's "0/4 due today" and a week headline of 0 looked like three
    answers to one question. Two of the three are the same question — Today counts what you *did*,
    the cell counts what you *met* — and the difference between them is entirely habits kept by
    doing nothing. So the cell now names its denominator, which is the same population Today counts
    out of on a day whose habits are all daily, and names the part of its count that Today cannot
    contain.

    Said here rather than in a legend because this is the surface where it is easiest to misread:
    the number sits on a shaded square with no other words near it. Bracketed rather than set off
    with a dash, which the title already spends on the date.
  */
  const abstinence =
    day.habitsMetByAbstinence > 0 ? ` (${day.habitsMetByAbstinence} by abstinence)` : '';
  /*
    A day with words on it says so, in the name as well as in the ring.

    The ring alone would be a colour-only distinction, which is the one thing `Icon.tsx`'s rule
    forbids and which a screen reader cannot see at all. It is appended rather than folded into the
    list above because it is not a count: "nothing recorded" and "a page with words" are both true
    of a quiet day somebody wrote about, and dropping either would lose the half the reader came
    for.
  */
  const page = day.hasPage ? ', page written' : '';
  const summary =
    day.tasksCompleted === 0 && day.habitsMet === 0 && day.habitsLogged === 0
      ? 'nothing recorded'
      : [
          day.tasksCompleted > 0 &&
            `${day.tasksCompleted} task${day.tasksCompleted === 1 ? '' : 's'}`,
          day.habitsMet > 0 &&
            `${day.habitsMet} of ${day.habitsDue} daily habit${day.habitsDue === 1 ? '' : 's'} met${abstinence}`,
          day.habitsLogged > 0 && `${day.habitsLogged} logged`,
        ]
          .filter(Boolean)
          .join(', ');

  return (
    <button
      type="button"
      className={`month-cell${day.inMonth ? '' : ' padding'}${selected ? ' selected' : ''}${
        day.hasPage ? ' has-page' : ''
      }`}
      onClick={onSelect}
      title={`${day.day} — ${summary}${page}`}
      aria-label={`${day.day}, ${summary}${page}`}
    >
      {day.habitsMet > 0 && (
        <span
          className="month-cell-heat"
          /*
            Capped well short of solid. At full strength `--ok` is dark enough that the date
            printed over it lost its own contrast — trading one unreadable cell for another. This
            range still separates the busiest day from the quietest across eight distinct steps on
            a real account, while leaving every date legible.
          */
          style={{ opacity: 0.12 + 0.45 * heat }}
          aria-hidden="true"
        />
      )}
      <span className="month-cell-date">{Number(day.day.slice(8))}</span>
      <span className="month-cell-dots">{dots}</span>
    </button>
  );
}

/** The one word on the pill that heads the `behind` line. */
const BEHIND_PILL = 'behind';

const PACE_WORD: Record<string, string> = {
  overdue: 'past its date',
  behind: BEHIND_PILL,
};

/**
 * " (past its date)", or nothing at all.
 *
 * `attention.behind` is two pace states under one pill: `behind` and `overdue` — see the filter in
 * `buildAttention`. So the bracket after a title is worth its space for an overdue goal, whose
 * state the pill cannot express, and worth nothing for a behind one, whose state the pill has
 * already given. It used to print unconditionally, which read "behind  Norway trip 2026 (behind)"
 * on every period and every page-back: the same fact twice in the same seven words.
 *
 * Compared against the pill's own word rather than against `'behind'` written out again, so a
 * relabelled pill takes its parenthetical with it.
 */
function paceNote(paceState: string | undefined): string {
  const word = PACE_WORD[paceState ?? ''] ?? paceState ?? '';
  return word === '' || word === BEHIND_PILL ? '' : ` (${word})`;
}

/**
 * How many rarely-met habits are worth naming before the count says it better.
 *
 * Thirteen names with their figures ran to eight wrapped lines on a 31-habit account, beside a
 * neighbouring line that says "4 behind" and stops. Three still reads as a list.
 */
const NAMED_STRUGGLING = 3;

/**
 * "Read Book (met 0 of the last 23 completed days)" — one rarely-met habit, with its window said.
 *
 * This read `(0 of 23), Run (0 of 2)`: the same sentence carrying two scales, 23 days for a daily
 * habit and 2 weeks for a weekly one, neither labelled. Worse, the bare ratio reads as being about
 * the period on the card — "(0 of 2)" on a week one day old looked like nonsense — when it is a
 * count of the habit's *own completed* periods behind it. Naming the unit is what tells those two
 * questions apart, and it is the same phrasing the habit's own page already uses for this number
 * ("Met in 0 of the last 23 days").
 *
 * Naming the unit was half of it. Two personas then read "Run (met 0 of 2 weeks)" here and "too
 * early to tell" on Run's own row a scroll below, and took the pair for two verdicts on one
 * question — which is what an unqualified denominator invites, because nothing in the sentence
 * said the two were looking at different stretches of time. "of the last N completed weeks" says
 * it: the row is about the week on the card, this is about the N before it, and a habit can
 * honestly be both. `MIN_PERIODS_FOR_TARGET_ADVICE` is what makes N worth printing.
 *
 * The unit comes off the payload rather than from the review's period: they differ, deliberately.
 * See `ReviewHabitHistory.period` in core.
 */
const struggling = (h: WeekReviewData['attention']['strugglingHabits'][number]): string =>
  `${h.name} (met ${h.periodsMet} of the last ${habitPeriodsCompleted(h.periodsConsidered, h.period)})`;

function WeekAttention({
  attention,
  habits,
  words,
}: {
  attention: WeekReviewData['attention'];
  /** The habit rows this same card renders — see `nothing`. */
  habits: WeekReviewData['habits'];
  words: ReturnType<typeof periodWords>;
}) {
  const { behind, stalled, untouched, strugglingHabits } = attention;
  /*
    The all-clear is a claim about the whole card, so it has to consult the whole card.

    It used to fire off an empty `attention` payload alone, and `attention` knows about goals and
    about habits with a *history* problem — not about a habit that is simply behind right now.
    So a quarter printed "Nothing is behind, stalled or slipping. This is the quarter reading as
    it should." directly above "0 of 11 habits met" and eleven red pills, three of which were
    Read pages 541/1840, Meditate 24/92 and Monthly mileage 72/390.

    `onTrack` is the same field the pill twenty rows below is coloured from, deliberately: if any
    pill on this card is red, there is no all-clear to give.
  */
  const behindNow = habits.filter((h) => !h.onTrack);
  const nothing =
    behind.length === 0 &&
    stalled.length === 0 &&
    strugglingHabits.length === 0 &&
    untouched.length === 0 &&
    behindNow.length === 0;

  if (nothing) {
    return (
      <p className="subtext" data-testid="week-attention-clear">
        Nothing is behind, stalled or slipping. This is the {words.period} reading as it should.
      </p>
    );
  }

  /*
    Habits behind and nothing else: the card says that better twenty rows down, so this says it
    not at all.

    `behindNow` used to print here as "behind · 3 of 3 habits". It still decides that there is no
    all-clear to give — a red pill anywhere on the card forbids one — but it no longer gets a line
    of its own, because the Habits list below names every one of them beside its own figure, and
    the comparison at the top of the card carries the count with its delta. Silence is a state this
    box did not have: it could say "all clear" or it could accuse, and a week with one lagging
    habit is neither.

    That was most of what made this card read as a charge sheet. The planner counted five `pill
    bad`, two amber and one green on a week in which four tasks and fifteen points were finished
    (both up on the week before), a goal went 0% → 100%, and the previous week's promise was kept
    — with the same three habits called out in five separate places.
  */
  if (
    behind.length === 0 &&
    stalled.length === 0 &&
    strugglingHabits.length === 0 &&
    untouched.length === 0
  ) {
    return null;
  }

  return (
    <div className="week-attention" data-testid="week-attention">
      {behind.length > 0 && (
        <p data-testid="attention-behind">
          <span className="pill bad">{BEHIND_PILL}</span>{' '}
          {behind.map((g) => `${g.title}${paceNote(g.paceState)}`).join(', ')}
        </p>
      )}
      {stalled.length > 0 && (
        <p data-testid="attention-stalled">
          <span className="pill warn">{NO_NEXT_STEP_LABEL}</span>{' '}
          {stalled.map((g) => g.title).join(', ')}
        </p>
      )}
      {strugglingHabits.length > 0 && (
        <p data-testid="attention-habits">
          <span className="pill warn">rarely met</span>{' '}
          {/*
            Names up to a handful, a count past it.

            On an account with 31 habits this read out thirteen of them with their figures and ran
            to eight wrapped lines — while the line directly above it says "4 behind" and stops.
            Two neighbouring statements about the same screen, one counting and one reciting.

            Three is where a list still reads as a list. Past that the count is the fact, and the
            per-habit figures stay reachable where they belong: on the habit itself, and in the
            title here so a hover still gives the whole set without spending the card on it.
          */}
          {strugglingHabits.length <= NAMED_STRUGGLING ? (
            strugglingHabits.map(struggling).join(', ')
          ) : (
            <span title={strugglingHabits.map(struggling).join(', ')}>
              {strugglingHabits.length} habits, including{' '}
              {strugglingHabits.slice(0, NAMED_STRUGGLING).map(struggling).join(', ')}
            </span>
          )}
          {' — '}
          <span className="subtext">worth changing the target rather than trying harder.</span>
        </p>
      )}
      {untouched.length > 0 && (
        <p className="subtext" data-testid="attention-untouched">
          No progress {words.thisOne} on {untouched.map((g) => g.title).join(', ')}.
        </p>
      )}
    </div>
  );
}

export function Reflection() {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [week, setWeek] = useState<string | undefined>(undefined);
  const [period, setPeriod] = useState<ReviewPeriod>('week');
  /* Every string that names a span comes from here — see `periodWords`. */
  const words = periodWords(period);

  const overview = useQuery({
    queryKey: ['month', month ?? 'current'],
    queryFn: () => getMonthOverview(month),
  });
  const review = useQuery({
    queryKey: ['week-review', week ?? 'current', period],
    queryFn: () => getWeekReview(week, period),
  });

  // The server answers with the month it actually resolved, so paging works before the
  // first response has told us what "this month" is.
  const shownMonth = overview.data?.month ?? month;

  /*
    The calendar follows the period being reviewed.

    Its month was independent state, so paging the review backwards left it behind: the card read
    "Week review 2026-07-20 → 2026-07-26" while the sidebar still said August 2026 with **no cell
    highlighted at all**. Five clicks of Previous week showed Aug 3–9, then Jul 27–Aug 2, then an
    empty selection the moment the reviewed week left August — and an empty selection reads as
    "nothing selected", not as "the selection is off-screen".

    Keyed on the reviewed start rather than synced on every render, so paging the calendar itself
    still works: it moves when the *review* moves, and stays where it is put otherwise.
  */
  const reviewedMonth = review.data?.start.slice(0, 7);
  const lastReviewed = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!reviewedMonth || reviewedMonth === lastReviewed.current) return;
    lastReviewed.current = reviewedMonth;
    setMonth(reviewedMonth);
  }, [reviewedMonth]);

  /*
    A period with no evidence in it is not scored — see the note above `PreviousDecision`.

    Two ways of arriving at the same place: `notStarted` is a period that has not happened, and a
    closed period with `noActivity` is one that happened without you. Only the first was ever acted
    on.

    `inProgress` is what separates the second from the week you are in the middle of. A live week
    that has recorded nothing *yet* is not a period without evidence — it is a period you can still
    act on, and blanking its habits and its decision prompt would take away the screen at the one
    moment it is useful. The finding was about Q1 2026, which the account predates.
  */
  const unscoreable =
    (review.data?.notStarted ?? false) ||
    ((review.data?.noActivity ?? false) && !(review.data?.inProgress ?? false));

  /*
    What the top of the chart is worth, computed where the legend can say it.

    Each series is scaled to its own peak — a month's task series dwarfs the habit one, and one
    shared denominator flattened every habit bar to `min-height: 2px`. Two peaks, named beside the
    two dots, rather than a drawn gridline that would belong to neither.
  */
  const chartPeaks = (() => {
    /*
      From the columns the bars are drawn from, not from the days underneath them.

      On a month or a quarter the chart buckets days into weeks, so a bar is a weekly sum while
      this was still taking the maximum of the *daily* rows. The legend announced "top 3" over a
      series whose tallest bar's own tooltip said 18 — a factor of six — and "TOP 1" over June
      weeks totalling five to seven.

      This is the same defect the comment above `habitsMet` describes being fixed for the headline,
      surviving in the legend: two numbers about one series, computed from different rows. The bars
      already scale to `Math.max(...columns)` a hundred lines below; the legend now names that
      number instead of a different one.

      And the series' own maximum, zero included (`01a0337a-1e5b`). This was `Math.max(1, …)` — the
      bars' guard against dividing by zero — so an empty series printed "TASKS DONE TOP 1" beside a
      headline of `0 Tasks` and a plot with no blue bar in it, over a week, a month and a quarter of
      zeros alike. The guard belongs to the division and stays there, below; the legend says `top 0`.
      `top 0` rather than dropping the entry: the zero ticks are still drawn in that series' colour,
      and the dot is what says which colour is which.
    */
    const data = review.data;
    if (!data) return { tasks: 0, habits: 0 };
    const columns = chartColumns(data.byDay, data.period, data.weekStartsOn, data.today);
    return {
      tasks: Math.max(0, ...columns.map((c) => c.completed)),
      habits: Math.max(0, ...columns.map((c) => c.habitsMet)),
    };
  })();

  return (
    <section className="reflection">
      {/*
        The review first, and the calendar after it.

        The grid is `3fr 2fr`, so whichever card comes first gets the wide column — and it was the
        month calendar. Measured by the UX reviewer: the calendar 492x490, the review 328x1391.
        Every number anyone opens this screen for sat in the narrow column, running 1391px tall,
        while a mostly-empty grid took the wide one and left roughly 900px blank beneath itself.

        Five personas arrived here independently, each wanting something different from the space —
        density, the encouraging numbers sooner, the habit table above the calendar — which is what
        made it the strongest signal in the review.

        Swapping the source order rather than reaching for `order` fixes the narrow case in the
        same move: below 900px the grid is one column, and a phone stopped scrolling past a month
        grid to reach the review.
      */}
      <div className="card" data-testid="review-card">
        <header className="row">
          {/* Named for what it is showing. Fixed text saying "Week review" over a quarter is the
              screen telling you the wrong thing in the largest type on it. */}
          <h2>{words.title} review</h2>
          {/* Words for the reader, *19–25 Oct*, and the ISO days on the element for anything that
              needs the machine form — a person never reads a `YYYY-MM-DD` (`01a0809a-3b90`). */}
          {review.data && (
            <span
              className="muted"
              data-testid="review-range"
              data-start={review.data.start}
              data-end={review.data.end}
            >
              {readableRange(review.data.start, review.data.end)}
            </span>
          )}
          {/*
            It had a label and nothing else, so the only way to change week was to click a cell in
            the month grid above — which means a week outside the displayed month was unreachable
            without paging the month first.
          */}
          {/*
            Week, month or quarter — the switcher this screen never had.

            Reflection offered a week and nothing else, so "am I getting better", the question the
            screen exists to answer, could not be asked. The layout below is unchanged: the same
            summary, the same habits, the same goals, over a longer span.

            Changing the period clears the anchor day. A day inside week 34 is inside some month
            and some quarter too, but paging back three weeks and then switching to quarters would
            silently take you to a quarter you did not choose.
          */}
          <div className="view-toggle" role="group" aria-label="Review period">
            {(['week', 'month', 'quarter'] as const).map((p) => (
              <button
                key={p}
                type="button"
                className={period === p ? 'is-active' : ''}
                aria-pressed={period === p}
                onClick={() => {
                  setPeriod(p);
                  setWeek(undefined);
                }}
              >
                {p[0]!.toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          <PeriodNav
            label={period}
            disabled={!review.data}
            onPrevious={() => setWeek(shiftPeriod(review.data!.start, -1, period))}
            onNext={() => setWeek(shiftPeriod(review.data!.start, 1, period))}
            onToday={() => setWeek(undefined)}
          />
        </header>

        <QueryState query={review} />
        {review.data && (
          <>
            {/*
              One sentence for a period that cannot be scored, and then nothing that scores it.

              `notStarted` and `noActivity` are two ways of arriving at the same place — there is
              no evidence here — and only the first of them was ever acted on. So paging Quarter
              back to Q1 2026 said "Nothing was recorded in this quarter, so there is nothing to
              score." and then scored it seven more times: a `behind` pill, an amber "no next
              step", "No progress this quarter on" seven goals, "behind 3 of 3 habits", "Nothing
              finished and no daily habit met", three red pills in the habit list, and "No goal
              moved this quarter". A screen that declines to judge and then judges anyway has said
              the second thing, whatever the first one claimed.

              The app already knew how to do this: paging *forward* suppressed the attention block
              entirely. This applies the same rule to a period that is over.

              The summary line goes with them. It reported "0 tasks done, 0 of 11 habits met, 3
              goals behind" — the sharpest scoring on the card — and for `notStarted` it merely
              repeated the notice below it in different words.
            */}
            {/* Above the tallies, where the confirmation printed on save says it will be. */}
            <PreviousDecision review={review.data} />

            {/*
              A period nobody used carries no score, and says so rather than flattering.

              Habits met by *absence* — an alcohol limit, no doomscrolling — are met by default in
              a stretch where nothing was recorded, so an unused quarter outscored the live week.
            */}
            {review.data.noActivity && (
              /* `.banner` is the API-error class; `.is-notice` is its grey variant, and this is a
                 statement of fact about an empty quarter rather than something going wrong. The
                 future-period notice five lines below has always carried it. */
              <p className="banner is-notice" data-testid="no-activity">
                {/*
                  Phase 41: this said "so there is nothing to score" for every empty period, while
                  everything it claimed to suppress is gated on `unscoreable` above — which
                  additionally requires the period to be closed. On the week you are living in the
                  two came apart, and the card printed a score directly under the sentence saying
                  there was none.

                  `unscoreable` is the half that is right: a live week with nothing in it yet is a
                  period you can still act on, not one without evidence. So the banner is what
                  changed. It still reports the emptiness, which is worth knowing, and no longer
                  draws a conclusion that the rest of the card contradicts.
                */}
                {review.data.inProgress
                  ? `Nothing recorded in this ${review.data.period} yet.`
                  : `Nothing was recorded in this ${review.data.period}, so there is nothing to score.`}
              </p>
            )}

            {/*
              Against the period before it, which is the only reason to hold a review.

              Nothing in the app compared anything against anything, so "am I getting better" —
              the question this screen exists to answer — could not be asked at all. Three
              headline numbers, each with what it was last time.
            */}
            {!unscoreable && <PeriodComparison review={review.data} />}

            {/*
              What to change, before what happened.
              
              Everything below this is bookkeeping — counts, a sparkline, percentages. The app
              has known which goals are behind, which have no next step and which habits are not
              really happening since Phases 13 and 15, and the one screen built for reflection
              showed none of it. A review that only totals output can report a productive week
              that moved nothing you cared about.
            */}
            {/*
              A week nobody has reached yet is not a review.

              `inProgress` was `today <= end`, which is true of the future as well as of the week
              being lived, and it only softened the attention list. So paging forward reported "3
              of 11 habits met" for an unlived week — an abstinence target of zero is clean until
              something breaks it, and a monthly total counted in every week it touched.
            */}
            {review.data.notStarted && (
              <p className="banner is-notice" data-testid="not-started">
                This {words.period} has not started yet. Nothing here has been met or missed.
              </p>
            )}

            {!unscoreable && (
              <WeekAttention
                attention={review.data.attention}
                habits={review.data.habits}
                words={words}
              />
            )}

            {/*
              Both axes of the app, and a heading saying so.

              This plotted `completed` — tasks — so an account that logs habits and finishes no
              tasks got an empty white band in every week with nothing to explain it. An unlabelled
              chart that can legitimately be empty is indistinguishable from a broken one, which is
              how two personas reported it.
            */}
            {!unscoreable && (
              <div className="week-chart" data-testid="week-chart">
                {/*
                  The legend carries the scale, because the legend is where each series is named.

                  The bars are drawn against a per-series peak with nothing saying what the peak is,
                  so the shape was readable and the magnitude was not — "is that spike three tasks
                  or thirty?" had no answer on the chart. The first cut put the peaks on a line of
                  their own and printed "daily habits met" a second time, which the card's
                  one-phrase-per-quantity spec caught: two names for one quantity is the defect
                  three phases of this screen have been about.
                */}
                <h3 className="chart-head">
                  {words.eachDay}
                  <span className="subtext" data-testid="spark-scale">
                    {' '}
                    <span className="dot task" /> tasks done{' '}
                    <span className="peak">top {chartPeaks.tasks}</span>{' '}
                    <span className="dot habit" /> {DAILY_HABITS_MET_LABEL}{' '}
                    <span className="peak">top {chartPeaks.habits}</span>
                  </span>
                </h3>
                {/* One track per column the period actually has — see `.sparkline`, which was
                    fixed at seven and rendered a quarter's 92 columns through the sections below. */}
                {(() => {
                  const columns = chartColumns(
                    review.data!.byDay,
                    review.data!.period,
                    review.data!.weekStartsOn,
                    review.data!.today,
                  );
                  /*
                    A denominator per series, not one shared between two units.

                    Tasks done and habits met were scaled against a single peak — the largest of
                    either, across the period. Over a week the two are comparable; over a month or
                    a quarter the task series dwarfed the habit one, so every habits-met bar fell
                    to `min-height: 2px` while its own tooltip read "13 habits met". Each series
                    against its own maximum: the shape within a series is what is being read, and
                    the two were never comparable in height anyway.

                    The legend's peaks, floored at 1 only here, where they divide: an all-zero
                    series draws its ticks at 0% either way, and the legend still says `top 0`.
                  */
                  const taskPeak = Math.max(1, chartPeaks.tasks);
                  const habitPeak = Math.max(1, chartPeaks.habits);
                  return (
                    <div
                      className="sparkline"
                      style={{ '--spark-days': columns.length } as React.CSSProperties}
                    >
                      {columns.map((c) => (
                        <div
                          key={c.key}
                          className={`spark-col ${c.unlived ? 'is-unlived' : ''}`}
                          title={c.title}
                        >
                          {/*
                            A day that has not happened draws nothing at all.

                            It used to draw the zero tick, so a quarter opened in week one showed
                            eleven of thirteen columns flat and read as a collapse. That is the
                            same mistake the headline stopped making in Phase 21 — a period nobody
                            has lived is not a period that scored nothing — and the column keeps a
                            faint floor so the axis still shows where it would be.
                          */}
                          {!c.unlived && (
                            <div className="spark-pair">
                              {/* Zero draws a tick, not a stub: a 2px bar for nothing and a 2px bar
                                for thirteen were the same picture. */}
                              <div
                                className={`spark-bar ${c.completed === 0 ? 'is-zero' : ''}`}
                                style={{ height: `${(c.completed / taskPeak) * 100}%` }}
                              />
                              <div
                                className={`spark-bar is-habit ${c.habitsMet === 0 ? 'is-zero' : ''}`}
                                style={{ height: `${(c.habitsMet / habitPeak) * 100}%` }}
                              />
                            </div>
                          )}
                          {/* A label on every column stops being readable long before a quarter, so
                              `chartColumns` decides which ones carry one. An unlabelled axis is
                              what a month and a quarter had. */}
                          {c.label !== '' && <span className="spark-label">{c.label}</span>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {review.data.byDay.every((d) => d.completed === 0 && d.habitsMet === 0) && (
                  <p className="muted">Nothing finished and no daily habit met {words.thisOne}.</p>
                )}
              </div>
            )}

            {!unscoreable && review.data.habits.length > 0 && (
              <>
                <h3>Habits</h3>
                <ul className="plain">
                  {review.data.habits.map((h) => (
                    <li key={h.id} className="review-row">
                      <span>{h.name}</span>
                      <span className={`pill ${verdictTone(h.verdict)}`} data-verdict={h.verdict}>
                        {/*
                          The one figure — see `habitFigure` — then the verdict, the same order as
                          every other habit row.

                          A limit is marked as one (*2 drinks of 3 allowed*), because a bare `10 /
                          26` reads like the same shortfall `46 / 91` does. The window is the one
                          the payload names, `h.period`, never the review's period or the habit's
                          cadence: the first produced *Monthly mileage 72 / 130 this week* beside a
                          week holding one 12 km entry. A habit shorter than the review is judged
                          across it and one longer keeps its own bounds.

                          This row printed `amount(actual) / target this period` — no unit, and for
                          a reading nobody took that week *0 / 14 this week* (`01a03813-b97d`).

                          The verdict through `verdictWord`, as on Today and the card: a reading
                          unread for two months said *too early to tell* here after those two had
                          stopped (`01a15d62-4f6d`). Judged over the row's own window, `h.period`,
                          for the reason above.
                        */}
                        {`${habitFigure(h, { window: h.period, asOf: reviewAsOf(review.data), today: review.data.today, approxTarget: h.scaledTarget })}${statedBasis(h)} · ${verdictWord(h, { period: h.period, asOf: reviewAsOf(review.data) })}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {!unscoreable && review.data.goals.length > 0 && (
              <ReviewGoals goals={review.data.goals} words={words} />
            )}

            {/* The notice above already says this, in a sentence that explains itself. */}
            {!unscoreable &&
              review.data.tasksCompleted === 0 &&
              review.data.habits.length === 0 &&
              review.data.goals.length === 0 && (
                <p className="muted">Nothing recorded for {words.thisOne}.</p>
              )}

            {/*
              One question, and it is the last thing on the screen.

              The review ended on a list, so the ritual ended with reading rather than deciding —
              which is the difference between a report and a review. This asks for exactly one
              thing to change, because a screen that asks for five gets none.

              It names the goal it is asking about rather than asking in the abstract: "what will
              you do about Sail the Baltic next week" is answerable, and "what will you change
              next week" is a blank page.
            */}
            {/*
              Not gated with the rest: this is the one block that is an input rather than a
              verdict. A period nobody used still gets asked what to change — that is arguably when
              the question matters most — and only a period that has not happened yet has nothing
              to decide about.
            */}
            {!review.data.notStarted && <WeekDecision review={review.data} />}
          </>
        )}
      </div>

      <div className="card">
        <header className="row">
          <h2>{shownMonth ? monthLabel(shownMonth) : 'This month'}</h2>
          <div className="row gap">
            <button
              className="ghost"
              disabled={!shownMonth}
              onClick={() => setMonth(shiftMonth(shownMonth!, -1))}
            >
              ← Previous
            </button>
            <button className="ghost" onClick={() => setMonth(undefined)}>
              Today
            </button>
            <button
              className="ghost"
              disabled={!shownMonth}
              onClick={() => setMonth(shiftMonth(shownMonth!, 1))}
            >
              Next →
            </button>
          </div>
        </header>

        <QueryState query={overview} />
        {overview.data && (
          <>
            <div className="month-weekdays">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>
            <div className="month-grid">
              {overview.data.days.map((day) => (
                <DayCell
                  key={day.day}
                  day={day}
                  peak={Math.max(1, ...overview.data.days.map((d) => d.habitsMet))}
                  selected={
                    review.data ? day.day >= review.data.start && day.day <= review.data.end : false
                  }
                  // Clicking a day reviews the week it belongs to — the month is for
                  // spotting the pattern, the week for reading what happened.
                  onSelect={() => setWeek(day.day)}
                />
              ))}
            </div>
            <p className="muted legend">
              <span className="dot task" /> tasks completed &nbsp;
              <span className="dot habit" /> {DAILY_HABITS_MET_LABEL}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * What moved, and then everything else.
 *
 * Every goal was listed, which for an account with fifteen of them means a dozen rows reading
 * `0% → 0%` and a dominant impression of a catalogue of things that did not happen. For the
 * persona most at risk of abandoning the app, this weekly ritual is the moment that decides
 * whether they come back — and it was reading like a list of failures.
 *
 * So: the ones that moved, largest change first, and the rest behind a disclosure. The rows are
 * unchanged and the copy is unchanged — this screen already has the best writing in the product —
 * it is the ordering and the cap that were saying the wrong thing.
 */
const MOVED_SHOWN = 3;

/**
 * The chart's columns, which are not always days, and which of them have happened.
 *
 * It was one column per day whatever the period. Measured: Week 7 columns at 68px with 7 labels;
 * Month 31 at 15px with **zero** labels; Quarter **92 at 4.4px with 1px bars** and zero labels —
 * a blank box with a hairline in it. Same component, more columns forced into 500px.
 *
 * A quarter is drawn in weeks, which is the unit somebody reviewing a quarter thinks in anyway,
 * and gets thirteen readable columns instead of ninety-two invisible ones. A month keeps its days
 * and gains a label every seventh, because a month of unlabelled columns cannot be read either.
 *
 * "Fifteen pixels is narrow but legible" was the rest of that sentence, and it was a 1280 number
 * describing a chart that no longer exists at 375: measured on the branch, the month chart is
 * 293px wide on a phone, so a column is **8.8px**. That is the number the label rule below has to
 * survive, and the old one did not.
 *
 * `unlived` is the same distinction the headline learned in Phase 21, one surface later: a period
 * nobody has lived is not a period that scored nothing. A quarter opened in its first week drew
 * eleven of its thirteen weekly columns at zero — because they are in the future — which reads as
 * a collapse. A real zero and a day that has not happened are different facts and now look it.
 *
 * @param today Today in the **account's** zone, from the review payload — never `todayIso()`.
 *
 * Both call sites used to pass `todayIso()`, the browser's day. Every bar here is cut in the
 * account's zone (`byDay` buckets on `(completed_at AT TIME ZONE $tz)::date`), so for an account
 * in a different zone the bars and the boundary drawn over them disagreed by one for part of every
 * day: a lived column drawn as a gap, or an unlived one drawn as a real zero. That is exactly the
 * misreading `unlived` was added to prevent.
 */
export function chartColumns(
  byDay: { day: string; completed: number; habitsMet: number; habitsLogged: number }[],
  period: 'week' | 'month' | 'quarter',
  weekStartsOn: number,
  today: string,
): {
  key: string;
  label: string;
  completed: number;
  habitsMet: number;
  title: string;
  unlived: boolean;
}[] {
  const monthName = (iso: string) => readableDay(iso, 'month');

  if (period === 'quarter') {
    /*
      Real weeks, not sevens from wherever the quarter happened to start.

      Slicing `byDay` into sevens put the first bucket on the quarter's own first day — 1 July, a
      Wednesday — and every bucket after it. So the column covering today aggregated Wed–Tue while
      the Week review beside it aggregated the account's own Mon–Sun, and the two reported three
      and six for what a reader would call the same week.

      `weekStartsOn` is the account's setting, taken from the same payload the review's own bounds
      were cut with, so the two cannot disagree.
    */
    const weeks: (typeof byDay)[] = [];
    let current: typeof byDay = [];
    for (const day of byDay) {
      /*
        Monday-based, matching `weekdayOf` in `packages/core/src/domain/periods.ts`.

        The codebase has two weekday conventions and only one of them is JavaScript's. `weekStartsOn`
        is measured against `(getUTCDay() + 6) % 7`, so `0` here means **Monday**, and comparing it
        against a bare `getUTCDay()` puts every boundary one day early — which is how the first
        version of this fix produced Sunday buckets under a Monday-start account.
      */
      const weekday = (new Date(`${day.day}T12:00:00Z`).getUTCDay() + 6) % 7;
      // A new bucket opens on the account's week start, and on the first day whatever it is.
      if (current.length > 0 && weekday === weekStartsOn) {
        weeks.push(current);
        current = [];
      }
      current.push(day);
    }
    if (current.length > 0) weeks.push(current);
    let lastMonth = '';
    return weeks.map((week) => {
      const first = week[0]!.day;
      const month = first.slice(0, 7);
      const label = month === lastMonth ? '' : monthName(first);
      lastMonth = month;
      const completed = week.reduce((n, d) => n + d.completed, 0);
      const habitsMet = week.reduce((n, d) => n + d.habitsMet, 0);
      /*
        A week is unlived once its *first* day is still ahead: a week you are part-way through has
        happened as far as it goes, and drawing it as a gap would hide the days you did live.

        And once nothing is known about any of it. "Unlived" means the day has not arrived **and**
        nothing is on record for it, which is the meaning the service adopted in `b766ec5`: a habit
        logged ahead, or an entry back-filled onto a future date, gives that day a real verdict, and
        a chart that blanks it on the date alone hides a figure the API has.
      */
      const unlived =
        first > today &&
        week.every((d) => d.completed === 0 && d.habitsMet === 0 && d.habitsLogged === 0);
      return {
        key: first,
        label,
        completed,
        habitsMet,
        unlived,
        title: unlived
          ? `Week of ${first}: not yet`
          : `Week of ${first}: ${completed} tasks done, ${habitsMet} ${DAILY_HABITS_MET_LABEL}`,
      };
    });
  }

  return byDay.map((d, i) => ({
    key: d.day,
    /*
      Every seventh column, and *not* also the last one.

      The month rule used to be `i % 7 === 0 || i === byDay.length - 1`, so a 30-day month labelled
      index 28 and index 29 — the 29th and the 30th, in adjacent columns. A two-digit label is
      14.2px wide and the column pitch on a phone is 9.8px, so the two rects could not both fit:
      measured at 375, `29` at 312.7..326.9 and `30` at 322.5..336.7, **overlapping by 4.4px**, and
      at 1280 they were 2.5px apart, which still reads as one number. A 31-day month put them two
      columns apart for a 4.8px gap — legal and no more readable.

      Dropped rather than thinned by measurement. "Drop the last label when it lands within a label
      width of the previous" needs the pitch, and the pitch is set by a card whose width changed
      twice in this phase alone; a rule counting columns is a bet on today's layout the same way a
      `rem` is a bet on today's strings. Nothing is lost by dropping it: the column keeps its date
      in `title`, and the card header prints the period's end date two lines above the chart.

      Week is unaffected — seven columns, every one labelled — and the quarter has its own branch.
    */
    label: period === 'week' || i % 7 === 0 ? d.day.slice(8) : '',
    completed: d.completed,
    habitsMet: d.habitsMet,
    // After today *and* nothing known about it — see the week bucket above.
    unlived: d.day > today && d.completed === 0 && d.habitsMet === 0 && d.habitsLogged === 0,
    title:
      d.day > today && d.completed === 0 && d.habitsMet === 0 && d.habitsLogged === 0
        ? `${d.day}: not yet`
        : `${d.day}: ${d.completed} tasks done, ${d.habitsMet} ${DAILY_HABITS_MET_LABEL}`,
  }));
}

function ReviewGoals({
  goals,
  words,
}: {
  /* The payload's own type, so this list cannot quietly stop seeing a field the headline uses. */
  goals: ReviewGoal[];
  words: ReturnType<typeof periodWords>;
}) {
  const [showAll, setShowAll] = useState(false);
  const delta = (g: { percentBefore: number; percentAfter: number }) =>
    g.percentAfter - g.percentBefore;

  /*
    Sorted by how far it moved, not by name.

    A goal that went 4% → 31% is the thing worth reading first, and nothing about alphabetical
    order or creation order would ever put it there.
  */
  /*
    One rule for "moved", used here and by the headline.

    v0.19.0 taught the headline that a goal carried by habits moves when its habits are logged —
    progress is computed from tasks, so such a goal is 0% before and 0% after. This section kept
    filtering on the percent delta alone, so the card read "2 goals moved" and, 700px below, "No
    goal moved this week". Two personas reported the contradiction independently.

    `goalMoved` is exported and the domain uses the same predicate, so the two cannot drift again.
  */
  const moved = goals.filter(goalMoved).sort((a, b) => delta(b) - delta(a));
  const still = goals.filter((g) => !goalMoved(g));
  const shown = showAll ? moved : moved.slice(0, MOVED_SHOWN);
  const hidden = goals.length - shown.length;

  return (
    <>
      <h3>Goals</h3>
      {moved.length === 0 ? (
        /*
          Said plainly rather than by printing a dozen unchanged rows and letting the reader work
          it out. A week where nothing moved is a fact about the week, not a fault in the goals.
        */
        <p className="muted">{words.noMovement}</p>
      ) : (
        <ul className="plain" data-testid="review-goals">
          {shown.map((g) => (
            <li key={g.id} className="review-row">
              <span>{g.title}</span>
              <span
                className={delta(g) > 0 ? 'delta up' : 'muted'}
                /*
                  Only the habit-moved row is explained; on a row that shows its own arithmetic
                  there is nothing a tooltip could add that the two percentages do not say.
                */
                title={
                  delta(g) === 0 && g.habitActivity
                    ? `A habit serving this goal was logged this ${words.period}. Progress is counted from tasks finished, so there is no percentage to move.`
                    : undefined
                }
              >
                {delta(g) === 0 && g.habitActivity ? (
                  /*
                    Its habits moved it, and a percentage computed from tasks cannot say so —
                    printing "0% → 0%" beside a goal listed as moved is the contradiction one
                    level down. That argument still holds; what changed is the wording.

                    This was the bare fragment "its habits": no verb, in the column where every
                    other row reads "0% → 100% (+100)", so the one row that cannot show a number
                    also stopped saying whether anything had happened. `MOVED_BY_HABITS_LABEL`
                    says it with the section's own word, and the title carries what the column has
                    no room for — that a habit was *logged*, which is not the same claim as met.
                  */
                  MOVED_BY_HABITS_LABEL
                ) : (
                  <>
                    {g.percentBefore}% → {g.percentAfter}%{delta(g) > 0 && ` (+${delta(g)})`}
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {showAll && still.length > 0 && (
        <ul className="plain" data-testid="review-goals-still">
          {still.map((g) => (
            <li key={g.id} className="review-row">
              <span className="muted">{g.title}</span>
              <span className="muted">{g.percentAfter}%</span>
            </li>
          ))}
        </ul>
      )}

      {/*
        Every goal the review reports on, and it says so through the same helper Today's link
        uses — that is the point of routing an unqualified count through it. The two links read
        "Show all 9 goals" here and "Show all 8 active goals" there over one account, which is
        the difference between the sets rather than a disagreement about the account.
      */}
      {hidden > 0 && !showAll && (
        <button className="link" onClick={() => setShowAll(true)}>
          {showAllGoalsLabel(goals.length, 'every')}
        </button>
      )}
      {showAll && (
        <button className="link" onClick={() => setShowAll(false)}>
          Show less
        </button>
      )}
    </>
  );
}

/**
 * Last period's answer, before this period's question.
 *
 * Two things were wrong with this and both were promises the card made and broke.
 *
 * It sat second from the bottom of a 1528px card — measured at y=1056, after every tally — while
 * the confirmation printed on save says, in as many words, "it will be at the top of your next
 * review". And pressing "Mark it done" renamed the note, which took it out of the readback's
 * exact-title lookup, so the block vanished on the press: no tick, no confirmation, no undo, and
 * gone from the period it was written in too. The one moment the ritual pays out was spending
 * itself on erasing the evidence.
 *
 * Above the counts, and ticked rather than removed.
 */
function PreviousDecision({ review }: { review: WeekReviewData }) {
  const words = periodWords(review.period);
  const queryClient = useQueryClient();
  /*
    Marking it done renames the note rather than setting a status.

    Notes have no status column and adding one for a single checkbox is a worse trade than a title
    that reads correctly on the Notes screen too. The readback looks for both titles, so the rename
    changes how this renders and not whether it renders. Writing a *new* decision supersedes the
    old one on its own, because the lookup is keyed on the period it came from.
  */
  const markDone = useMutation({
    mutationFn: (id: string) => updateNote(id, { title: review.previousDecision!.doneTitle }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (!review.previousDecision) return null;

  return (
    <div
      className={`previous-decision ${review.previousDecision.done ? 'is-done' : ''}`}
      data-testid="previous-decision"
    >
      <h4>Last {words.period}, you said</h4>
      <blockquote>{review.previousDecision.bodyMd}</blockquote>
      {review.previousDecision.done ? (
        <p className="subtext" data-testid="previous-decision-done">
          <span className="pill ok">done</span> You said you would, and you did.
        </p>
      ) : (
        <button
          type="button"
          className="link"
          disabled={markDone.isPending}
          onClick={() => markDone.mutate(review.previousDecision!.id)}
        >
          Mark it done
        </button>
      )}
    </div>
  );
}

/**
 * The one thing to change next week.
 *
 * The week review ended on a list of goals, so the weekly ritual ended in reading rather than in
 * deciding — which is the whole difference between a report and a review.
 *
 * The subject is chosen rather than asked for: the goal needing attention, or failing that the
 * one that moved most. "What will you do about Sail the Baltic?" is answerable; "what will you
 * change next week?" is a blank page, and a blank page at the end of a weekly ritual is where the
 * ritual stops.
 *
 * The answer is a note against the week, which is where the rest of this screen's writing already
 * lives — nothing new to store and nothing new to find later.
 */
function WeekDecision({ review }: { review: WeekReviewData }) {
  const words = periodWords(review.period);
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);

  /*
    Behind first, then stalled, then whatever moved most.

    `attention` already separates the goals the app has an opinion about, and asking "what will you
    do about the one that is behind" is a better question than asking about the one that happened
    to move furthest — which is usually the one going well.
  */
  const subject =
    review.attention.behind[0]?.title ??
    review.attention.stalled[0]?.title ??
    [...review.goals].sort(
      (a, b) => b.percentAfter - b.percentBefore - (a.percentAfter - a.percentBefore),
    )[0]?.title ??
    null;

  /*
    Stored as an ordinary note, titled with the week it came out of.

    Notes have no notion of a period and did not need one for this: a title carrying the date is
    what makes it findable later, and `searchEverything` already indexes notes, so next week's
    decision turns up when you search for the goal it was about.
  */
  const save = useMutation({
    mutationFn: (body: string) => createNote({ title: review.decisionTitle, bodyMd: body }),
    onSuccess: () => {
      setSaved(true);
      setText('');
      void queryClient.invalidateQueries();
    },
  });

  if (saved) {
    return (
      <p className="muted week-decision" data-testid="week-decision">
        {/* It is true now. It was not before: the note was written and nothing ever read it. */}
        Saved. It will be at the top of your {words.next.toLowerCase().replace('next ', '')} review.
      </p>
    );
  }

  return (
    <form
      className="week-decision"
      data-testid="week-decision"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) save.mutate(text.trim());
      }}
    >
      <h3>{words.next}</h3>
      <label htmlFor="week-decision-input">
        {subject
          ? `One thing to change about “${subject}” — or anything else.`
          : `One thing to change ${words.next.toLowerCase()}.`}
      </label>
      <textarea
        id="week-decision-input"
        rows={2}
        value={text}
        placeholder="Drop it, halve it, or book an hour for it."
        onChange={(e) => setText(e.target.value)}
      />
      <button type="submit" disabled={!text.trim() || save.isPending}>
        Save it
      </button>
    </form>
  );
}

/**
 * This period against the one before it.
 *
 * A delta is only worth printing when there is something to compare against: a first-ever week
 * has a previous period of zeroes, and "+4 tasks" against a period the user was not using the app
 * in is a flattering lie. So a previous period with nothing recorded says so instead.
 *
 * More is not automatically better and the wording does not pretend otherwise — the number and
 * its direction are stated, and what to make of them is the reader's business. That is the same
 * position the rest of this screen's copy takes.
 */
function PeriodComparison({ review }: { review: WeekReviewData }) {
  const { previous } = review;
  const caption = comparisonCaption(review);
  const empty =
    previous.tasksCompleted === 0 &&
    previous.pointsCompleted === 0 &&
    previous.habitsConsidered === 0;

  /*
    A running period compares against the previous one at the same point in it — for all three
    rows, not just habits.

    Phase 47 did this for habits and left tasks and points reading against a finished period, so
    the card said `0 Tasks −4` on a Monday against a week whose four tasks were all done on the
    Wednesday. The caption below already prints the truncated range, which made an honest label sit
    above a dishonest number.
  */
  const soFar = <T,>(running: T | undefined, whole: T): T =>
    review.inProgress ? (running ?? whole) : whole;

  const rows = [
    {
      label: 'Tasks',
      now: review.tasksCompleted,
      was: soFar(previous.tasksCompletedSoFar, previous.tasksCompleted),
    },
    {
      label: 'Points',
      now: review.pointsCompleted,
      was: soFar(previous.pointsCompletedSoFar, previous.pointsCompleted),
    },
    /* Named for what it measures: habits at their target *for this period*. The chart below
       counts habits met *per day*, which is a different question with the same old label — see
       `DAILY_HABITS_MET_LABEL`. Both names now come from `periodWords`, so the headline and this
       tile cannot drift apart again: they did, and this was renamed while the headline was not. */
    {
      label: `Habits ${periodWords(review.period).habitsMet}`,
      now: review.habitsMet,
      /*
        A running period compares against the previous one *at the same point in it*.

        `previous.habitsMet` is how last month ended, which is the right comparison once this month
        has ended too and the wrong one on the 23rd: almost nothing has reached a monthly target
        yet, while last month had all 31 days to get there, so the delta swung on the calendar
        alone — "1 Habits at their month target −4" for a reader doing exactly as well as before.

        `previous.habitsMet` stays untouched and faithful, because the delta has to match the card
        you reach by pressing "‹". This is a second reading beside it.
      */
      was: soFar(previous.habitsMetSoFar, previous.habitsMet),
      /*
        And how many of them could not be judged at all, which is why the count can be low.

        "0 Habits at their week target −2" sat above two green *at target* pills, and the −2 was a
        deficit against a week whose habits had simply not been measured. The count now comes from
        the rows themselves, so the two agree; this says what the rest of the number is, and
        suppresses a delta that would be comparing a measured period with an unmeasured one.
      */
      note: review.habitsTooEarly > 0 ? `${review.habitsTooEarly} too early` : null,
    },
  ];

  return (
    <div className="period-comparison" data-testid="period-comparison">
      {rows.map((r) => {
        const d = r.now - r.was;
        const note = 'note' in r ? r.note : null;
        return (
          <div key={r.label} className="comparison-figure">
            <span className="comparison-value">{r.now}</span>
            <span className="comparison-label">{r.label}</span>
            {note ? (
              /* A period with unjudged habits in it has no delta worth printing — see `note`. */
              <span className="subtext" data-testid="comparison-note">
                {note}
              </span>
            ) : empty ? (
              <span className="subtext">no earlier {review.period}</span>
            ) : (
              <span className={d > 0 ? 'delta up' : d < 0 ? 'delta down' : 'subtext'}>
                {d === 0 ? 'same' : `${d > 0 ? '+' : ''}${d}`}
              </span>
            )}
          </div>
        );
      })}
      {/*
        Both stretches, not just the one on the right — see `comparisonCaption`.

        This printed the prior range alone: "against 2026-07-01 to 2026-07-23" on the 23rd of
        August, exact and unreadable. The only range on the card with a shape to compare it against
        was the header's *whole* current period, so a reader saw 31 days measured against 23 and
        read the second date as a bug. Saying which part of this period was counted is the half
        that was missing; the half that was already right stays untouched.

        The caption is built rather than written out here so that every shape it has to survive can
        be run against it in one place — a month whose previous month is shorter, a quarter against
        a longer one, a period nobody has reached. `comparisonCaption` carries the reasoning and
        `comparisonCaption.spec.ts` carries the eleven shapes.
      */}
      {!empty && caption && <p className="subtext comparison-against">{caption}</p>}
    </div>
  );
}
